import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import {
  IFacilityShiftList,
  IFetchAllShiftsParams,
  IShiftHistoryState,
  IShiftStatistics,
  ITodayShiftStatistics,
  IShiftDetails,
  ICancelShiftResponse,
  IUpdateShiftAttendanceRequest,
  IUpdateShiftAttendanceResponse,
  IOrganizationActionOnShiftAttendanceRequest,
  IBulkOrganizationActionOnShiftAttendanceRequest,
  IOngoingShiftList,
  IForceCancelShiftResponse,
  IFetchMonitoringShiftsParams,
  IMonitoringShiftList,
  IShiftRequestList,
  IUpdateShiftRequestStatusResponse,
  ICompleteShiftRequestResponse,
  ICancelShiftRequestPayload,
  ICancelShiftRequestResponse,
  ICancellationWarningResponse,
  IValidateWorkerShiftTimingRequest,
  IValidateWorkerShiftTimingResponse,
  IShiftBoardStats,
  IFetchShiftBoardStatsParams,
} from './types';
import apiCall from 'utils/api';

export const organizationClockIn = createAsyncThunk(
  'shift/organizationClockIn',
  async (assignmentId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ status: boolean; message: string }>(
        'post',
        `/api/shifts/attendance/organization/clock-in/${assignmentId}/`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clock in shift via organization');
    }
  },
);

export const organizationClockOut = createAsyncThunk(
  'shift/organizationClockOut',
  async (assignmentId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ status: boolean; message: string }>(
        'post',
        `/api/shifts/attendance/organization/clock-out/${assignmentId}/`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clock out shift via organization');
    }
  },
);

export const fetchOngoingShifts = createAsyncThunk(
  'shift/fetchOngoingShifts',
  async (
    {
      facilityId,
      page,
      pageSize,
    }: {
      facilityId: number;
      page: number;
      pageSize: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<{ data: IOngoingShiftList }>(
        'get',
        `/api/shifts/ongoing/${facilityId}/?page=${page}&page_size=${pageSize}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch ongoing shifts',
      );
    }
  },
);

export const fetchShiftDetails = createAsyncThunk(
  'shift/fetchShiftDetails',
  async (shiftId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IShiftDetails }>(
        'get',
        `/api/shifts/details/${shiftId}/`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch shift details');
    }
  },
);

export const fetchTodayShiftStatistics = createAsyncThunk(
  'shift/fetchTodayShiftStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: ITodayShiftStatistics }>(
        'get',
        '/api/organization/statistics/shifts/today/',
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch today shift statistics');
    }
  },
);

export const fetchShiftStatistics = createAsyncThunk(
  'shift/fetchShiftStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IShiftStatistics }>(
        'get',
        '/api/organization/statistics/shifts/',
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch shift statistics');
    }
  },
);

export const fetchFacilityShiftList = createAsyncThunk(
  'facility/fetchFacilityShiftList',
  async ({
    facilityId,
    page,
    pageSize,
    orderBy,
    order,
  }: {
    facilityId: number,
    page: number;
    pageSize: number;
    orderBy?: string;
    order?: 'asc' | 'desc';
  }, { rejectWithValue }) => {
    try {
      let url = `/api/shifts/facility/${facilityId}/?page=${page}&page_size=${pageSize}`;

      if (orderBy) {
        url += `&order_by=${orderBy}`;
      }
      if (order) {
        url += `&order=${order}`;
      }

      const response = await apiCall<{ data: IFacilityShiftList }>('get', url);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility list');
    }
  }
);

export const fetchOrgFacilityPendingShifts = createAsyncThunk(
  'shift/fetchOrgFacilityPendingShifts',
  async ({
    page,
    pageSize,
    search,
  }: {
    page: number;
    pageSize: number;
    search?: string;
  }, { rejectWithValue }) => {
    try {
      let url = `/api/shifts/facility-pending/organization/?page=${page}&page_size=${pageSize}`;
      if (search) {
        url += `&search=${search}`;
      }
      const response = await apiCall<{ data: IFacilityShiftList }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending shifts list');
    }
  }
);

export const fetchAllShifts = createAsyncThunk(
  'shift/fetchAllShifts',
  async (params: IFetchAllShiftsParams, { rejectWithValue }) => {
    try {
      let url = '/api/shifts/all-shifts/?';
      const queryParams = new URLSearchParams();

      if (params.status) {
        queryParams.append('status', params.status);
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }
      if (params.page_size) {
        queryParams.append('page_size', params.page_size.toString());
      }
      if (params.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params.orderby) {
        queryParams.append('orderby', params.orderby);
      }
      if (params.order) {
        queryParams.append('order', params.order);
      }

      url += queryParams.toString();

      const response = await apiCall<{ data: IShiftHistoryState }>('get', url);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch shift history');
    }
  }
);

export const cancelShift = createAsyncThunk(
  'shift/cancelShift',
  async (
    { shiftId, reason, additionalReason }: { shiftId: number; reason: string; additionalReason?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<ICancelShiftResponse>(
        'patch',
        `/api/shifts/cancel/${shiftId}/`,
        {
          cancellation_reason: reason,
          additional_cancel_reason: additionalReason,
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel shift');
    }
  },
);

export const updateShiftAttendance = createAsyncThunk(
  'shift/updateShiftAttendance',
  async (
    { shiftId, data }: { shiftId: number; data: IUpdateShiftAttendanceRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IUpdateShiftAttendanceResponse>(
        'put',
        `/api/shifts/attendance/action/${shiftId}/`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update shift attendance');
    }
  },
);

export const organizationActionOnShiftAttendance = createAsyncThunk(
  'shift/organizationActionOnShiftAttendance',
  async (
    data: IOrganizationActionOnShiftAttendanceRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IUpdateShiftAttendanceResponse>(
        'put',
        `/api/shifts/attendance/organization-action/${data.shift_id}/`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update shift attendance via organization action');
    }
  },
);

export const bulkOrganizationApproveAttendance = createAsyncThunk(
  'shift/bulkOrganizationApproveAttendance',
  async (
    data: IBulkOrganizationActionOnShiftAttendanceRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IUpdateShiftAttendanceResponse>(
        'put',
        '/api/shifts/attendance/organization-bulk-action/',
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk approve shift attendance');
    }
  },
);

export const forceCancelShift = createAsyncThunk(
  'shift/forceCancelShift',
  async (
    { shiftId, cancellation_reason }: { shiftId: number; cancellation_reason: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IForceCancelShiftResponse>(
        'post',
        '/api/organization/shifts/force-cancel/',
        {
          shift_id: shiftId,
          cancellation_reason: cancellation_reason,
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to force cancel shift');
    }
  },
);

export const fetchMonitoringShifts = createAsyncThunk(
  'shift/fetchMonitoringShifts',
  async (params: IFetchMonitoringShiftsParams, { rejectWithValue }) => {
    try {
      let url = '/api/organization/shifts/monitoring/?';
      const queryParams = new URLSearchParams();

      queryParams.append('start_date', params.start_date);
      queryParams.append('end_date', params.end_date);

      if (params.facility && params.facility !== 'All Facilities') {
        queryParams.append('facility', params.facility.toString());
      }
      if (params.worker_id) {
        queryParams.append('worker_id', params.worker_id.toString());
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }
      if (params.page) {
        queryParams.append('page', params.page.toString());
      }
      // Default huge page size if not provided, to mimic "recursive fetch" or just get all
      queryParams.append('page_size', (params.page_size || 1000).toString());

      url += queryParams.toString();

      const response = await apiCall<{ data: IMonitoringShiftList }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch monitoring shifts');
    }
  },
);

export const fetchShiftRequests = createAsyncThunk(
  'shift/fetchShiftRequests',
  async (
    {
      orgId,
      page,
      pageSize,
    }: {
      orgId: number;
      page: number;
      pageSize: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<{ data: IShiftRequestList }>(
        'get',
        `/api/shifts/shift-requests/organization/${orgId}/?page=${page}&page_size=${pageSize}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch shift requests');
    }
  },
);

export const fetchFacilityShiftRequests = createAsyncThunk(
  'shift/fetchFacilityShiftRequests',
  async (
    {
      page,
      pageSize,
    }: {
      page: number;
      pageSize: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<{ data: IShiftRequestList }>(
        'get',
        `/api/shifts/shift-requests/facility/?page=${page}&page_size=${pageSize}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility shift requests');
    }
  },
);

export const updateShiftRequestStatus = createAsyncThunk(
  'shift/updateShiftRequestStatus',
  async (
    shiftRequestId: number,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IUpdateShiftRequestStatusResponse>(
        'patch',
        `/api/shifts/shift-requests/update-status/${shiftRequestId}/`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update shift request status');
    }
  },
);

export const completeShiftRequest = createAsyncThunk(
  'shift/completeShiftRequest',
  async (
    shiftRequestId: number,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<ICompleteShiftRequestResponse>(
        'post',
        `/api/shifts/shift-requests/complete/${shiftRequestId}/`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to complete shift request');
    }
  },
);

export const cancelShiftRequest = createAsyncThunk(
  'shift/cancelShiftRequest',
  async (
    { shiftRequestId, data }: { shiftRequestId: number; data: ICancelShiftRequestPayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<ICancelShiftRequestResponse>(
        'patch',
        `/api/shifts/shift-requests/cancel/${shiftRequestId}/`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel shift request');
    }
  },
);

export const resetUpdateShiftRequestStatus = createAction('shift/resetUpdateShiftRequestStatus');
export const resetCompleteShiftRequest = createAction('shift/resetCompleteShiftRequest');
export const resetUpdateShiftAttendance = createAction('shift/resetUpdateShiftAttendance');
export const resetCancelShiftRequest = createAction('shift/resetCancelShiftRequest');
export const resetForceCancelShift = createAction('shift/resetForceCancelShift');
export const resetOrganizationClockIn = createAction('shift/resetOrganizationClockIn');
export const resetOrganizationClockOut = createAction('shift/resetOrganizationClockOut');

export const fetchCancellationWarning = createAsyncThunk(
  'shift/fetchCancellationWarning',
  async (shiftId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<ICancellationWarningResponse>(
        'get',
        `/api/shifts/cancellation-warning/${shiftId}/`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cancellation warning');
    }
  },
);

export const validateWorkerShiftTiming = createAsyncThunk(
  'shift/validateWorkerShiftTiming',
  async (
    data: IValidateWorkerShiftTimingRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IValidateWorkerShiftTimingResponse>(
        'post',
        '/api/worker/validate-worker-shift-timing/',
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to validate worker shift timing');
    }
  },
);

export const fetchShiftBoardStats = createAsyncThunk(
  'shift/fetchShiftBoardStats',
  async (params: IFetchShiftBoardStatsParams, { rejectWithValue }) => {
    try {
      let url = '/api/shifts/stats/details/?';
      const queryParams = new URLSearchParams();

      if (params.facility_id) {
        queryParams.append('facility_id', params.facility_id.toString());
      }
      if (params.worker_id) {
        queryParams.append('worker_id', params.worker_id.toString());
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }

      url += queryParams.toString();

      const response = await apiCall<{ data: IShiftBoardStats }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch shift board stats');
    }
  },
);

